import type {
  ApolloClient,
  ApolloError,
  NormalizedCache,
} from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { useCallback, useState } from 'react';

import type {
  AuthenticationInfoQuery as Query,
  AuthenticationInfoQueryVariables as Variables,
} from '../generated';
import { AuthenticationInfoDocument as Document } from '../generated';

const query = async <TCacheShape = NormalizedCache>(
  client: ApolloClient<TCacheShape>,
  variables: Variables,
) =>
  client.query<Query, Variables>({
    query: Document,
    variables,
  });

const useQuery = () => {
  const client = useApolloClient();
  const [currentData, setCurrentData] = useState<Query | null>(null);
  const [currentError, setCurrentError] = useState<ApolloError | null>(null);
  const [loading, setLoading] = useState(false);

  const runQuery = useCallback(
    async (variables: Variables) => {
      try {
        setLoading(true);

        const result = await query(client, variables);

        setCurrentError(null);
        setCurrentData(result.data);

        return result;
      } catch (error) {
        // See DS-846
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setCurrentError(error);

        throw error;
      } finally {
        setLoading(false);
      }
    },
    [client],
  );

  return [
    runQuery,
    { data: currentData, error: currentError, loading },
  ] as const;
};

export { query, useQuery };
